<script setup lang="ts">
import type { FetchError } from 'ofetch'

import { emailValidator, requiredValidator } from '~/utils/validators'

import type { User } from '#auth-utils'

definePageMeta({
  layout: 'focus',
  middleware: 'unauthenticated',
})

const { fetch, user } = useUserSession()
const toast = useToast()
const credentials = ref<
  Nullable<{
    email: string
    password: string
    requiredEmailValidation: boolean
  }>
>({
  email: null,
  password: null,
  requiredEmailValidation: null,
})

const v$ = useValidation(
  () => ({
    email: { required: requiredValidator, email: emailValidator },
    password: { required: requiredValidator },
  }),
  credentials,
  {
    $scope: 'auth',
    $registerAs: 'login',
  }
)

const isLoading = ref(false)

const login = useDebounceFn(
  async () => {
    if (!(await v$.value.$validate())) return
    try {
      isLoading.value = true
      const { email, password } = credentials.value
      const result = await $fetch<NuxtServerResponse<User, FetchError>>('/api/login', {
        method: 'post',
        body: { email, password },
      })
      if (result.error) {
        isLoading.value = false
        toast.add({
          severity: 'warn',
          summary: 'Autenticación',
          detail: 'Credenciales incorrectas',
          life: 5000,
        })
        return
      }
      await fetch()
      if (user.value?.emailConfirmed) {
        window.location.replace('/')
        return
      } else {
        credentials.value.requiredEmailValidation = true
      }
      isLoading.value = false
    } catch {
      isLoading.value = false
      toast.add({
        severity: 'error',
        summary: 'Autenticación',
        detail: 'Hubo un error, intentelo de nuevo más tarde, disculpe los inconvenientes.',
        life: 5000,
      })
    }
  },
  500,
  { maxWait: 60000 }
)

watch(
  () => credentials.value.requiredEmailValidation,
  (n, o) => {
    if (!n && o) {
      window.location.replace('/')
    }
  }
)
</script>

<template>
  <div class="flex flex-col items-center justify-center w-full lg:justify-center">
    <CardComponent
      class="w-full md:max-w-md"
      bg="light"
    >
      <template #header>
        <div class="py-4">
          <ImageComponent
            src="/img/logo-contigo.png"
            alt="Contigo"
            class="h-14"
          />
        </div>
      </template>
      <div class="flex flex-col gap-4 p-4">
        <ProcessingComponent
          v-if="isLoading"
          loading-message="Autenticando..."
        />

        <template v-else-if="credentials.requiredEmailValidation && user?.id">
          <AuthEmailConfirmationForm
            v-model="credentials.requiredEmailValidation"
            :user-id="user.id"
            :email="credentials.email"
            is-login
          />
        </template>
        <template v-else>
          <div class="my-4 flex flex-col gap-2">
            <InputField
              label="Correo Electrónico"
              :error="v$.email?.$error"
              :error-message="v$.email?.$errors[0]?.$message.toString()"
            >
              <PvInputText
                v-model.lowercase="credentials.email"
                modif
                @keydown.enter="login()"
              />
            </InputField>
            <InputField
              label="Contraseña"
              :error="v$.password?.$error"
              :error-message="v$.password?.$errors[0]?.$message.toString()"
            >
              <PvPassword
                v-model="credentials.password"
                :feedback="false"
                fluid
                toggle-mask
                @keydown.enter="login()"
              />
            </InputField>
          </div>
          <PvButton
            :loading="isLoading"
            style-as-input
            @click="login"
          >
            Iniciar sesión
          </PvButton>
          <PvButton
            severity="contrast"
            as="a"
            style-as-input
            href="/user/forgot-password"
          >
            Olvide mi contraseña
          </PvButton>
          <div class="flex">
            <PvDivider />
            <span class="text-nowrap px-2">No tienes una cuenta?</span>
            <PvDivider />
          </div>
          <PvButton
            severity="contrast"
            as="a"
            style-as-input
            href="/user/register"
          >
            Registrate
          </PvButton>
        </template>
      </div>
    </CardComponent>
  </div>
</template>
